import React, { useMemo } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildModelTrendData,
  getTickInterval,
  MODEL_LINE_COLORS,
  ModelTrendTooltip,
  OTHER_MODEL_COLOR,
} from "./chartUtils";

const ACCENT = "#26890d";
const BLUE = "#0097D7";
const OVERAGE_AMBER = "#e53835";

// List price per AIC credit — same rate used for "Gross Spend" on the StatCards.
// Total spend here means the full dollar cost of gross usage, not just overage.
const LIST_PRICE_PER_AIC = 0.01;

const withTotalSpend = (monthly) =>
  (monthly ?? []).map((item) => ({
    ...item,
    total_spend: (item.total_gross_credits ?? 0) * LIST_PRICE_PER_AIC,
  }));

// Pool coverage % isn't returned by the API — derived here from the gross/pool-covered
// totals the monthly endpoint already provides per month.
const buildPoolTrendData = (monthly) => {
  if (!monthly?.length) return [];
  return monthly.map((item) => {
    const gross = item.total_gross_credits ?? 0;
    const poolCovered = item.total_pool_covered_credits ?? 0;
    // Clamped to [0, 100] — the API has returned pool_covered_credits slightly
    // exceeding gross_credits for some months, which would otherwise push this
    // over 100% and break the chart's fixed y-axis domain.
    const pct = gross > 0 ? (poolCovered / gross) * 100 : 0;
    return {
      label: item.label,
      poolCoveredPct: Math.min(100, Math.max(0, pct)),
    };
  });
};

const PoolTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const pct = payload[0]?.value ?? 0;
  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e8e8e8",
        borderRadius: "8px",
        p: "10px 14px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#555", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: pct >= 30 ? ACCENT : OVERAGE_AMBER }}>
        {pct.toFixed(1)}% pool covered
      </Typography>
    </Box>
  );
};

const ChartCard = ({ title, subtitle, fromDate, toDate, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: "12px",
      border: "1px solid #e8e8e8",
      backgroundColor: "#fff",
      padding: "24px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
      <Box>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#999", mb: 2.5 }}>
          {subtitle}
        </Typography>
      </Box>
      {fromDate && toDate && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.2,
            py: 0.35,
            backgroundColor: "#f7f7f7",
            border: "1px solid #e8e8e8",
            borderRadius: "20px",
          }}
        >
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#777" }}>
            {fromDate} → {toDate}
          </Typography>
        </Box>
      )}
    </Box>
    {children}
  </Paper>
);

const CombinedTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    const aicPoint = payload.find((item) => item.dataKey === "total_gross_credits");
    const spendPoint = payload.find((item) => item.dataKey === "total_spend");

    return (
      <Box
        sx={{
          background: "#fff",
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          p: "10px 14px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#555", mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: ACCENT }}>
          {(aicPoint?.value ?? 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })} AIC
        </Typography>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: BLUE }}>
          ${
            (spendPoint?.value ?? 0).toLocaleString("en-AU", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          }
        </Typography>
      </Box>
    );
  }
  return null;
};

const UsageCharts = ({
  data,
  dailyRecords,
  fromDate,
  toDate,
  showMonthlyTrend = true,
  showModelUsageTrend = true,
  showPoolCoverageTrend = true,
}) => {
  const monthlyWithSpend = useMemo(() => withTotalSpend(data), [data]);

  const { rows: modelTrendRows, modelKeys } = useMemo(
    () => buildModelTrendData(dailyRecords),
    [dailyRecords]
  );
  const poolTrendRows = useMemo(() => buildPoolTrendData(data), [data]);
  const modelTrendTickInterval = getTickInterval(modelTrendRows.length);

  return (
    <Grid container spacing={2}>
      {/* Combined AIC + Spend Chart */}
      {showMonthlyTrend && (
      <Grid item xs={12}>
        <ChartCard
          title="AIC Consumed + Total Spend - Monthly Trend"
          subtitle="Last 12 months, independent of the date range above; hover to compare monthly credits and spend"
        >
          <Box sx={{ "& svg, & svg *": { outline: "none" } }}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={monthlyWithSpend} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="aicGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ACCENT} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                }
                width={45}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                width={55}
              />
              <Tooltip content={<CombinedTooltip />} cursor={{ stroke: "#cfcfcf", strokeWidth: 1, strokeDasharray: "4 4" }} />
              <Legend
                wrapperStyle={{ fontSize: "0.75rem" }}
                formatter={(value) => <span style={{ color: "#555" }}>{value}</span>}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="total_gross_credits"
                name="AIC Consumed"
                stroke={ACCENT}
                strokeWidth={2.5}
                fill="url(#aicGradient)"
                dot={{ fill: ACCENT, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: ACCENT, strokeWidth: 0 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="total_spend"
                name="Total Spend"
                stroke={BLUE}
                strokeWidth={2.5}
                dot={{ fill: BLUE, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: BLUE, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
          </Box>
        </ChartCard>
      </Grid>
      )}

      {/* Per-model daily trend */}
      {showModelUsageTrend && modelTrendRows.length > 0 && (
        <Grid item xs={12}>
          <ChartCard
            title="Usage Grouped by Model"
            subtitle="Daily gross AIC consumed per model — hover to see spend in USD"
            fromDate={fromDate}
            toDate={toDate}
          >
            <Box sx={{ "& svg, & svg *": { outline: "none" } }}>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={modelTrendRows} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                    interval={modelTrendTickInterval}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                    width={45}
                  />
                  <Tooltip content={<ModelTrendTooltip />} cursor={{ stroke: "#cfcfcf", strokeWidth: 1, strokeDasharray: "4 4" }} />
                  <Legend
                    wrapperStyle={{ fontSize: "0.75rem" }}
                    formatter={(value) => <span style={{ color: "#555" }}>{value}</span>}
                  />
                  {modelKeys.map((model, index) => (
                    <Line
                      key={model}
                      type="monotone"
                      dataKey={model}
                      stroke={model === "All other" ? OTHER_MODEL_COLOR : MODEL_LINE_COLORS[index % MODEL_LINE_COLORS.length]}
                      strokeWidth={2}
                      strokeDasharray={model === "All other" ? "3 3" : undefined}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 0 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>
      )}

      {/* Pool coverage trend */}
      {showPoolCoverageTrend && poolTrendRows.length > 0 && (
        <Grid item xs={12}>
          <ChartCard
            title="Pool Coverage Trend"
            subtitle="% of gross AIC usage covered by the enterprise pool, per month — last 12 months, independent of the date range above"
          >
            <Box sx={{ "& svg, & svg *": { outline: "none" } }}>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={poolTrendRows} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, 100]}
                    width={40}
                  />
                  <Tooltip content={<PoolTrendTooltip />} cursor={{ stroke: "#cfcfcf", strokeWidth: 1, strokeDasharray: "4 4" }} />
                  <Line
                    type="monotone"
                    dataKey="poolCoveredPct"
                    stroke={ACCENT}
                    strokeWidth={2.5}
                    dot={{ fill: ACCENT, r: 3, strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: ACCENT, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>
      )}
    </Grid>
  );
};

export default UsageCharts;
